import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface PageTopBarConfig {
  breadcrumb?: string[];
  actions?: React.ReactNode;
  /** Pass when action props/state change so the shell top bar re-renders. */
  actionsVersion?: string | number;
}

interface PageTopBarContextValue {
  breadcrumb?: string[];
  actionsRef: React.RefObject<React.ReactNode>;
  actionsTick: number;
  setBreadcrumb: (breadcrumb: string[] | undefined) => void;
  syncActions: () => void;
}

const PageTopBarContext = createContext<PageTopBarContextValue | null>(null);

function breadcrumbKey(breadcrumb: string[] | undefined): string {
  return breadcrumb?.length ? JSON.stringify(breadcrumb) : '';
}

export function PageTopBarProvider({ children }: { children: React.ReactNode }) {
  const [breadcrumb, setBreadcrumbState] = useState<string[] | undefined>();
  const [actionsTick, setActionsTick] = useState(0);
  const actionsRef = useRef<React.ReactNode>(null);

  const setBreadcrumb = useCallback((next: string[] | undefined) => {
    setBreadcrumbState((prev) => {
      const prevKey = breadcrumbKey(prev);
      const nextKey = breadcrumbKey(next);
      return prevKey === nextKey ? prev : next;
    });
  }, []);

  const syncActions = useCallback(() => {
    setActionsTick((t) => t + 1);
  }, []);

  const value = useMemo((): PageTopBarContextValue => {
    const next: PageTopBarContextValue = { actionsRef, actionsTick, setBreadcrumb, syncActions };
    if (breadcrumb !== undefined) next.breadcrumb = breadcrumb;
    return next;
  }, [breadcrumb, actionsTick, setBreadcrumb, syncActions]);

  return <PageTopBarContext.Provider value={value}>{children}</PageTopBarContext.Provider>;
}

export function usePageTopBarContext() {
  const ctx = useContext(PageTopBarContext);
  if (!ctx) {
    throw new Error('usePageTopBarContext must be used within PageTopBarProvider');
  }
  return ctx;
}

export function usePageTopBar(config: PageTopBarConfig) {
  const { actionsRef, setBreadcrumb, syncActions } = usePageTopBarContext();
  actionsRef.current = config.actions ?? null;

  const key = breadcrumbKey(config.breadcrumb);

  useLayoutEffect(() => {
    setBreadcrumb(key ? (JSON.parse(key) as string[]) : undefined);
    syncActions();
    return () => {
      setBreadcrumb(undefined);
    };
  }, [setBreadcrumb, syncActions, key]);

  useLayoutEffect(() => {
    syncActions();
  }, [syncActions, config.actionsVersion]);
}
